import * as React from 'react';

import {Button} from 'shells/button/button';
import {ShowHideButton} from 'shells/button/show-hide-button/show-hide-button';
import {ErrorState} from 'shells/error-state/error-state';
// @ts-expect-error
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
import {IconStatus} from 'shells/icon-status/icon-status';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {LegacySetupTableContainer} from 'shells/table/legacy-setup-table-container';
import {LegacySetupTableWrapper} from 'shells/table/legacy-setup-table-wrapper';
import {Table} from 'shells/table/table';
import {AddButtonRow} from 'shells/table/table-body/add-button-row';
import type {Row} from 'shells/table/types';

import type {DomainFragment} from 'nutshell-graphql-types';

import {DomainDetails} from './domain-details';
import {DomainRowActionMenu} from './domain-row-action-menu';
import {getDomainIconStatus} from './helpers';

type Props = {
    domains: DomainFragment[];
    isLoading: boolean;
    error: string | null | undefined;
    onAddRowClick: () => void;
    onDeleteDomain: (domainName: string) => Promise<any>;
    expandRowIndex?: number | null | undefined;
    clearExpandRowIndex?: () => void;
    handleEntriRetry: (domainName: string) => void;
    domainInEntriFlow: string | null | undefined;
};

export function DomainsTable(props: Props) {
    const {onDeleteDomain, handleEntriRetry, domainInEntriFlow} = props;

    const columns = React.useMemo(
        () => [
            {
                id: 'name',
                Header: 'URL',
                // @ts-expect-error
                accessor: (row) => row.name,
                dataType: 'component',
                width: 200,
                Cell: ({cell}: any) => (
                    <Button
                        title={cell.value}
                        variant='text-primary'
                        noPadding={true}
                        size='small'
                        className='truncate'
                        onClick={() => cell.row.toggleRowExpanded(!cell.row.isExpanded)}
                    >
                        {cell.value}
                    </Button>
                ),
            },
            {
                id: 'status',
                // @ts-expect-error
                accessor: (row) => {
                    return {
                        domainName: row.name,
                        statusIconProps: getDomainIconStatus(
                            row.emailDeliverabilityStatus,
                            row.hostingTrackingStatus
                        ),
                        allowEntriRetry: row.canUseEntriSetup,
                    };
                },
                dataType: 'component',
                Cell: ({cell}: any) => (
                    <div className='flex gap-8'>
                        <IconStatus {...cell.value.statusIconProps} />
                        {cell.value.allowEntriRetry &&
                            (domainInEntriFlow === cell.value.domainName ? (
                                <LoadingIcon size={17} className='flex pl-16' />
                            ) : (
                                <Button
                                    variant='text-primary'
                                    size='small'
                                    noPadding={true}
                                    disabled={Boolean(domainInEntriFlow)}
                                    onClick={() => {
                                        handleEntriRetry(cell.value.domainName);
                                    }}
                                >
                                    Configure
                                </Button>
                            ))}
                    </div>
                ),
            },
        ],
        [handleEntriRetry, domainInEntriFlow]
    );

    const firstRow = <AddButtonRow buttonText='Add new domain' onClick={props.onAddRowClick} />;

    const renderRowActionMenu = (row: Row<any>) => (
        <DomainRowActionMenu
            domainName={row.original.name}
            onDeleteDomain={() => onDeleteDomain(row.original.name)}
            toggleRowExpanded={row.toggleRowExpanded}
        />
    );

    return (
        <LegacySetupTableWrapper title='Domains' count={props.domains.length}>
            <LegacySetupTableContainer>
                <Table
                    firstRow={firstRow}
                    hasHeaderCellBorders={true}
                    // @ts-expect-error
                    columns={columns}
                    data={props.domains}
                    renderRowActionMenu={renderRowActionMenu}
                    expandProps={{
                        renderExpandCell: renderRowExpandCell,
                        renderSubComponent: renderRowSubComponent,
                        expandRowIndex: props.expandRowIndex,
                        clearExpandRowIndex: props.clearExpandRowIndex,
                    }}
                    disableSorting={true}
                    disableFilters={true}
                    fullWidth={true}
                    specialStates={[
                        {
                            shouldRender: () => Boolean(props.error),
                            component: <ErrorState errorMessage={props.error} />,
                        },
                        {
                            shouldRender: () => props.isLoading,
                            component: <LoadingPage padding={32} />,
                        },
                    ]}
                />
            </LegacySetupTableContainer>
        </LegacySetupTableWrapper>
    );
}

const renderRowExpandCell = (row: Row<any>): React.ReactElement => {
    return (
        <ShowHideButton
            showText='Details'
            hideText='Details'
            isShown={Boolean(row.isExpanded)}
            onToggle={() => row.toggleRowExpanded(!row.isExpanded)}
        />
    );
};

const renderRowSubComponent = (row: Row<any>): React.ReactElement => (
    <DomainDetails domain={row.original} toggleRowExpanded={row.toggleRowExpanded} />
);
